import React from 'react';
type Suit = '♠' | '♥' | '♦' | '♣';
type Rank = '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K' | 'A';
interface PlayingCardProps {
  rank?: Rank | string;
  suit?: Suit | string;
  selected?: boolean;
  onClick?: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  faceDown?: boolean;
  disabled?: boolean;
  highlighted?: boolean;
  dimmed?: boolean;
}
const suitColor = (suit: string): string => {
  return suit === '♥' || suit === '♦' ? '#cc2222' : '#1a1a2e';
};
const suitName = (suit: string): string => {
  const names: Record<string, string> = {
    '♠': 'Spades',
    '♥': 'Hearts',
    '♦': 'Diamonds',
    '♣': 'Clubs'
  };
  return names[suit] || suit;
};
const sizeConfig = {
  xs: {
    width: 36,
    height: 52,
    rankSize: 10,
    suitSize: 14,
    cornerSize: 8
  },
  sm: {
    width: 52,
    height: 74,
    rankSize: 13,
    suitSize: 18,
    cornerSize: 10
  },
  md: {
    width: 70,
    height: 100,
    rankSize: 16,
    suitSize: 24,
    cornerSize: 12
  },
  lg: {
    width: 90,
    height: 128,
    rankSize: 20,
    suitSize: 32,
    cornerSize: 14
  },
  xl: {
    width: 110,
    height: 156,
    rankSize: 24,
    suitSize: 40,
    cornerSize: 16
  }
};
export function PlayingCard({
  rank = 'A',
  suit = '♠',
  selected = false,
  onClick,
  size = 'md',
  faceDown = false,
  disabled = false,
  highlighted = false,
  dimmed = false
}: PlayingCardProps) {
  const cfg = sizeConfig[size];
  const color = suitColor(suit);
  const isRed = suit === '♥' || suit === '♦';
  const cardStyle: React.CSSProperties = {
    width: cfg.width,
    height: cfg.height,
    borderRadius: Math.max(6, cfg.width * 0.08),
    flexShrink: 0,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: Math.max(4, cfg.width * 0.07),
    cursor: onClick && !disabled ? 'pointer' : 'default',
    userSelect: 'none',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease',
    opacity: dimmed ? 0.4 : 1
  };
  if (faceDown) {
    return <div style={{
      ...cardStyle,
      background: 'linear-gradient(135deg, #2d1b5e 0%, #1a0a3e 50%, #2d1b5e 100%)',
      border: '1px solid #4a2d8a',
      boxShadow: '2px 2px 8px rgba(0,0,0,0.5)',
      alignItems: 'center',
      justifyContent: 'center'
    }} onClick={onClick} data-id="element-244">
        <div style={{
        width: '80%',
        height: '80%',
        border: '1px solid rgba(180,79,255,0.3)',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'repeating-linear-gradient(45deg, rgba(180,79,255,0.05) 0px, rgba(180,79,255,0.05) 2px, transparent 2px, transparent 8px)'
      }} data-id="element-245">
          <span style={{
          fontSize: cfg.suitSize * 0.6,
          color: 'rgba(180,79,255,0.4)'
        }} data-id="element-246">
            🃏
          </span>
        </div>
      </div>;
  }
  const selectedStyle: React.CSSProperties = selected ? {
    transform: 'translateY(-14px)',
    boxShadow: `0 0 0 2px #FFD700, 0 0 20px rgba(255,215,0,0.5), 2px 14px 20px rgba(0,0,0,0.7)`
  } : highlighted ? {
    boxShadow: `0 0 0 2px #4fc3f7, 0 0 15px rgba(79,195,247,0.4)`
  } : {
    boxShadow: '2px 2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.8)'
  };
  return <div style={{
    ...cardStyle,
    ...selectedStyle,
    background: '#fdf6e3',
    border: '1px solid #d4c5a0'
  }} onClick={!disabled ? onClick : undefined} role={onClick ? 'button' : undefined} aria-label={`${rank} of ${suitName(suit)}${selected ? ', selected' : ''}`} aria-pressed={onClick ? selected : undefined} data-id="element-247">
      {/* Top-left corner */}
      <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: 1
    }} data-id="element-248">
        <span style={{
        fontSize: cfg.rankSize,
        fontWeight: 700,
        color,
        fontFamily: 'Courier Prime, monospace',
        lineHeight: 1
      }} data-id="element-249">
          {rank}
        </span>
        <span style={{
        fontSize: cfg.cornerSize,
        color,
        lineHeight: 1
      }} data-id="element-250">
          {suit}
        </span>
      </div>

      {/* Center suit */}
      <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: cfg.suitSize,
      color,
      lineHeight: 1,
      textAlign: 'center'
    }} data-id="element-251">
        {suit}
      </div>

      {/* Bottom-right corner (rotated) */}
      <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: 1,
      transform: 'rotate(180deg)',
      alignSelf: 'flex-end'
    }} data-id="element-252">
        <span style={{
        fontSize: cfg.rankSize,
        fontWeight: 700,
        color,
        fontFamily: 'Courier Prime, monospace',
        lineHeight: 1
      }} data-id="element-253">
          {rank}
        </span>
        <span style={{
        fontSize: cfg.cornerSize,
        color,
        lineHeight: 1
      }} data-id="element-254">
          {suit}
        </span>
      </div>
    </div>;
}